import { useCallback, useEffect, useRef, useState } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { FORMAT_TEXT_COMMAND, $getSelection, $isRangeSelection, $createParagraphNode } from 'lexical'
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from '@lexical/list'
import { $setBlocksType } from '@lexical/selection'
import { $createHeadingNode, $isHeadingNode } from '@lexical/rich-text'
import { TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link'
import { $findMatchingParent } from '@lexical/utils'

const BLOCK_TYPES = [
  { value: 'paragraph', label: 'Normal' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
]

const IconBold = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z" />
  </svg>
)

const IconItalic = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z" />
  </svg>
)

const IconUnderline = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z" />
  </svg>
)

const IconStrikethrough = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 19h4v-3h-4v3zM5 4v3h5v3h4V7h5V4H5zM3 14h18v-2H3v2z" />
  </svg>
)

const IconBulletList = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z" />
  </svg>
)

const IconNumberList = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z" />
  </svg>
)

const IconLink = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
  </svg>
)

const ToolbarPlugin = () => {
  const [editor] = useLexicalComposerContext()
  const [blockType, setBlockType] = useState('paragraph')
  const [showBlockMenu, setShowBlockMenu] = useState(false)
  const blockMenuRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (blockMenuRef.current && !blockMenuRef.current.contains(e.target)) {
        setShowBlockMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) return
        const anchorNode = selection.anchor.getNode()
        const element =
          anchorNode.getKey() === 'root'
            ? anchorNode
            : $findMatchingParent(anchorNode, (node) => node.getParent()?.getKey() === 'root') || anchorNode.getTopLevelElementOrThrow()
        if ($isHeadingNode(element)) {
          setBlockType(element.getTag())
        } else {
          setBlockType(element.getType())
        }
      })
    })
  }, [editor])

  const formatText = useCallback(
    (format) => {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
    },
    [editor],
  )

  const handleBlockTypeChange = useCallback(
    (type) => {
      setShowBlockMenu(false)
      editor.update(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) return
        if (type === 'paragraph') {
          $setBlocksType(selection, () => $createParagraphNode())
        } else {
          $setBlocksType(selection, () => $createHeadingNode(type))
        }
      })
    },
    [editor],
  )

  const insertLink = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return
      const node = selection.anchor.getNode()
      const parent = node.getParent()
      if ($isLinkNode(parent)) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
      } else {
        const url = prompt('Enter URL:')
        if (url) {
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, url)
        }
      }
    })
  }, [editor])

  return (
    <div className="editor-toolbar">
      <div className="block-type-dropdown" ref={blockMenuRef}>
        <button type="button" className="block-type-trigger" onClick={() => setShowBlockMenu((prev) => !prev)} aria-label="Text type">
          {BLOCK_TYPES.find((bt) => bt.value === blockType)?.label || 'Normal'}
          <span className="dropdown-arrow">▾</span>
        </button>
        {showBlockMenu && (
          <ul className="block-type-menu">
            {BLOCK_TYPES.map((bt) => (
              <li key={bt.value}>
                <button type="button" className={blockType === bt.value ? 'active' : ''} onClick={() => handleBlockTypeChange(bt.value)}>
                  {bt.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <span className="toolbar-divider" />
      <button type="button" onClick={() => formatText('bold')} aria-label="Bold" title="Bold">
        <IconBold />
      </button>
      <button type="button" onClick={() => formatText('italic')} aria-label="Italic" title="Italic">
        <IconItalic />
      </button>
      <button type="button" onClick={() => formatText('underline')} aria-label="Underline" title="Underline">
        <IconUnderline />
      </button>
      <button type="button" onClick={() => formatText('strikethrough')} aria-label="Strikethrough" title="Strikethrough">
        <IconStrikethrough />
      </button>
      <span className="toolbar-divider" />
      <button type="button" onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)} aria-label="Bullet List" title="Bullet List">
        <IconBulletList />
      </button>
      <button type="button" onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)} aria-label="Numbered List" title="Numbered List">
        <IconNumberList />
      </button>
      <span className="toolbar-divider" />
      <button type="button" onClick={insertLink} aria-label="Insert Link" title="Insert Link">
        <IconLink />
      </button>
    </div>
  )
}

export default ToolbarPlugin
